import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import {
  IconAngleRight,
  IconCheckDefault,
  IconCopyOutline,
  IconShieldCheck,
} from '@iwoca/icons';
import { Link, Modal } from '@iwoca/orion';

import styles from './ShareableLinkModal.module.css';
import { useGetUserConsent } from '../../../../api/lendingApi/lendingApiQueries';
import { usePostOpenBankingShareableLink } from '../../../../api/openBankingShareableLinkApi/usePostOpenBankingShareableLink';
import { useUserConsentMutation } from '../../../../api/userConsentApi/useUserConsentMutation';
import { useClipboard } from '../../../../hooks/useClipboard';
import { useContextStateKey } from '../../../../hooks/useContextStateKey';
import logger from '../../../../utils/logger';
import { Button } from '../../../Button/Button';

const TRUE_LAYER_OPEN_BANKING_PROVIDER = 'truelayer_open_banking';

type TProps = {
  isOpen: boolean;
  onClose: () => void;
};

const ShareableLinkModal = ({ isOpen, onClose }: TProps) => {
  const [step, setStep] = useState<number>(1);
  const { isCopied: linkCopied, copyToClipboard, reset } = useClipboard();

  const stateKey = useContextStateKey();
  const { userConsent } = useGetUserConsent();
  const { postUserConsent } = useUserConsentMutation();

  const { mutateAsync, isPending, isError, data } =
    usePostOpenBankingShareableLink();

  const handleAgreeAndGenerateLink = async () => {
    if (!stateKey) return;

    try {
      await mutateAsync({ stateKey });
    } catch (error) {
      return logger.error('Failed to generate share link', {
        error,
      });
    }

    if (!userConsent) {
      postUserConsent({
        body: {
          data: {
            bundle_hash: viteBuildHash,
          },
        },
        provider: TRUE_LAYER_OPEN_BANKING_PROVIDER,
        stateKey: stateKey as string,
      });
    }

    setStep(3);
  };

  const handleClose = () => {
    setStep(1);
    reset();
    onClose();
  };

  const handleCopy = () => {
    const clipboardText = data ? data.link : '';
    copyToClipboard(clipboardText).then(() => {
      tracking.ampli.trueLayerGenerateShareLinkClicked({
        url: data?.link,
      });
    });
  };

  const getModalSubTitle = () => {
    if (step === 1) {
      return 'Authorise and share a secure link with someone else to link your account.';
    }

    return 'Authorise and share a secure link with someone else to complete your document requirements.';
  };

  const renderStep1 = () => (
    <>
      <ol className={styles.stepsList}>
        <li className={styles.step}>
          <span className={styles.stepNumber}>1</span>
          <div className={styles.stepContent}>
            <span className={styles.stepTitle}>Get started</span>
            <span className={styles.stepDescription}>
              Select ‘Get my secure shareable link’ below
            </span>
          </div>
        </li>
        <li className={styles.step}>
          <span className={styles.stepNumber}>2</span>
          <div className={styles.stepContent}>
            <span className={styles.stepTitle}>Authorise</span>
            <span className={styles.stepDescription}>
              Agree to share your banking data with iwoca
            </span>
          </div>
        </li>
        <li className={styles.step}>
          <span className={styles.stepNumber}>3</span>
          <div className={styles.stepContent}>
            <span className={styles.stepTitle}>Copy and paste the link</span>
            <span className={styles.stepDescription}>
              Share the secure link with your trusted third party however you
              like.
            </span>
          </div>
        </li>
        <li className={styles.step}>
          <span className={styles.stepNumber}>4</span>
          <div className={styles.stepContent}>
            <span className={styles.stepTitle}>Wait for confirmation</span>
            <span className={styles.stepDescription}>
              Once they’ve linked your bank account we’ll let you know via
              email.
            </span>
          </div>
        </li>
      </ol>
      <Button className={styles.ctaButton} onClick={() => setStep(2)}>
        Get my secure shareable link
      </Button>
    </>
  );

  const renderStep2 = () => (
    <>
      <p className={styles.consentIntro}>
        In order to generate your secure shareable link, we need you to provide
        consent to iwoca that we can use your banking data.
      </p>
      <div className={styles.detailsCard}>
        <h3 className={styles.detailsTitle}>What details am I sharing?</h3>
        <p className={styles.detailsSubtitle}>
          The following information will be shared with iwoca LTD:
        </p>
        <ul className={styles.detailsList}>
          <li>Full name</li>
          <li>Account number and sort code</li>
          <li>Balance</li>
          <li>Transactions direct debits and standing orders.</li>
        </ul>
        <div className={styles.encryptionCard}>
          <div className={styles.encryptionHeader}>
            <IconShieldCheck className={styles.shieldIcon} />
            <span className={styles.encryptionTitle}>
              Bank-grade encryption
            </span>
          </div>
          <p className={styles.encryptionText}>
            We use a partner called Truelayer, to access the information needed
            to assess your ability to repay a loan.
          </p>
          <p className={styles.encryptionText}>
            Truelayer needs your permission to access your information for 90
            days.
          </p>
          <p className={styles.encryptionText}>
            Truelayer is FCA regulated, and won’t share or use your personal
            data for anything else.
          </p>
        </div>
      </div>
      <Button
        className={styles.ctaButton}
        onClick={handleAgreeAndGenerateLink}
        disabled={isPending}
        loading={isPending}
      >
        Agree and generate shareable link
      </Button>
      {isError && (
        <p className={styles.errorMessage}>
          Something went wrong. Please try again.
        </p>
      )}
      <p className={styles.termsText}>
        By selecting ‘Agree and generate shareable link’, you agree to{' '}
        <Link
          href="https://truelayer.com/legal/enduser_tos/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.termsTextLink}
        >
          Truelayer’s Terms of Service
        </Link>{' '}
        and{' '}
        <Link
          href="https://truelayer.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.termsTextLink}
        >
          Privacy Policy
        </Link>
        .
      </p>
    </>
  );

  const renderStep3 = () => {
    return (
      <>
        <div className={styles.successMessage}>
          <IconCheckDefault className={styles.successIcon} />
          <span>Thanks for agreeing. Your secure link is below.</span>
        </div>
        <div className={styles.linkCard}>
          <h3 className={styles.linkCardTitle}>Your secure shareable link</h3>
          <p className={styles.linkCardSubtitle}>
            (This link will be active for 90 days)
          </p>
          <div className={styles.linkRow}>
            <span className={styles.linkText}>{data ? data.link : ''}</span>
            {linkCopied ? (
              <div className={styles.linkCopiedMessage}>
                Link copied
                <IconCheckDefault className={styles.linkCopiedIcon} />
              </div>
            ) : (
              <Button className={styles.copyButton} onClick={handleCopy}>
                Copy link
                <IconCopyOutline className={styles.copyIcon} />
              </Button>
            )}
          </div>
        </div>
        <Link
          href="/link-open-banking/preview-link-bank"
          target="_blank"
          rel="noopener noreferrer"
        >
          What will the recipient see when they access the link?
          <IconAngleRight className={styles.angleRightIcon} />
        </Link>
      </>
    );
  };
  return (
    <Modal
      className={styles.modalContainer}
      isOpen={isOpen}
      onClose={handleClose}
      title="Share a link for someone else to help"
      subTitle={getModalSubTitle()}
      closeIcon
      headerBar
    >
      <div className={styles.modalContents}>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
    </Modal>
  );
};

export default ShareableLinkModal;
